import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const FAQS = [
  { q: 'What is the best CUET preparation strategy?', a: 'The most effective strategy combines syllabus analysis, NCERT-based concept building, topic-wise practice, regular mock tests, and consistent revision — sustained over several months rather than compressed into a few weeks.' },
  { q: 'How should I prepare for CUET from scratch?', a: 'Start by understanding the exam pattern and syllabus, then build a realistic study plan, work through NCERT fundamentals, and introduce mock tests once initial concepts are covered.' },
  { q: 'Can I prepare for CUET at home?', a: 'Yes, CUET preparation can be done entirely at home with the right structure — a study plan, quality resources, and regular self-testing, ideally supplemented with guided coaching for feedback.' },
  { q: 'Is NCERT enough for CUET preparation?', a: 'NCERT forms a strong foundation for most domain subjects, but pairing it with previous year papers and mock tests helps close the gap between concept knowledge and exam-specific performance.' },
  { q: 'How many months are enough to prepare for CUET?', a: 'Most students benefit from at least 4–6 months of structured preparation, depending on their starting point and the number of domain subjects involved.' },
  { q: 'Should I solve previous year question papers?', a: "Yes, PYQs are one of the most reliable ways to understand NTA's actual question patterns and frequently tested topics." },
  { q: 'Are mock tests important for CUET?', a: 'Yes, regular mock tests build the timing, stamina, and familiarity that are difficult to develop through untimed practice alone.' },
  { q: 'How many hours should I study daily for CUET?', a: 'This varies by student, but consistency matters more than raw hours — a sustainable 2–3 hour daily routine over several months tends to outperform sporadic long sessions.' },
  { q: 'Can I prepare for CUET along with my Board exams?', a: 'Yes, with a realistic schedule that allocates time to both rather than treating them as competing priorities.' },
  { q: 'Which books are best for CUET preparation?', a: 'NCERT textbooks remain the primary resource for domain subjects, supplemented with previous year papers and structured practice material.' },
  { q: 'Is online coaching helpful for CUET preparation?', a: 'Yes, particularly for structure, expert guidance, and consistent mock testing — areas that are hard to replicate reliably through self-study alone.' },
  { q: 'How does BESST help students prepare for CUET?', a: "BESST provides live classes, structured study plans, comprehensive study materials, regular mock tests, and personalised mentorship, built around the preparation strategy outlined in this guide." },
  { q: 'Does BESST provide mock tests and study materials?', a: "Yes, both are included as part of BESST's CUET coaching programme." },
  { q: 'Can beginners crack CUET with proper planning?', a: 'Yes, a structured plan built around syllabus understanding, consistent practice, and regular testing can work well even for students starting from a beginner level, given enough preparation time.' },
  { q: 'What should be my preparation strategy in the last month before CUET?', a: 'Focus on full-length mock tests, targeted revision of weak areas identified through earlier testing, and consolidating what has already been learned rather than introducing new topics.' },
];

const STEPS = [
  { n: '01', icon: '🔍', title: 'Analyse the Syllabus', desc: 'Break the syllabus down by subject and topic before creating any study schedule, so the plan is built around what\'s actually being tested.' },
  { n: '02', icon: '📅', title: 'Create a Realistic Study Plan', desc: 'Set a schedule that accounts for board exam preparation too — an unrealistic plan gets abandoned within two weeks.' },
  { n: '03', icon: '📚', title: 'Build Strong NCERT Fundamentals', desc: 'NCERT remains the base for most domain subjects in CUET — concepts built here transfer directly into exam performance.' },
  { n: '04', icon: '✏️', title: 'Practise Topic-wise Questions', desc: 'After covering a topic, immediately reinforce it with focused practice questions rather than moving on to the next topic untested.' },
  { n: '05', icon: '📄', title: 'Solve Previous Year Papers', desc: 'PYQs reveal patterns in how NTA actually frames questions — patterns that are hard to predict from syllabus documents alone.' },
  { n: '06', icon: '📝', title: 'Take Weekly Mock Tests', desc: 'Regular, timed mock tests build the stamina and pacing that a single end-of-syllabus mock cannot replicate.' },
  { n: '07', icon: '🔎', title: 'Analyse Your Mistakes', desc: 'Every mock test should end with a review — not just of the score, but of exactly which questions were missed and why.' },
  { n: '08', icon: '🔄', title: 'Revise Regularly', desc: 'Spaced, recurring revision keeps earlier topics from fading by the time the exam arrives.' },
];

const BESST_FEATURES = [
  { icon: '📡', title: 'Live Interactive Classes', desc: 'Real-time teaching across every CUET section, with the ability to ask questions as they come up.' },
  { icon: '🗓️', title: 'Structured Study Plans', desc: 'Personalised schedules that put the step-by-step strategy above into an actual weekly routine.' },
  { icon: '📚', title: 'Comprehensive Study Materials', desc: 'Syllabus-aligned notes and practice material, removing the need to search for reliable resources independently.' },
  { icon: '🎓', title: 'Expert Faculty', desc: 'Faculty with specific CUET teaching experience, not a generalised approach adapted from other exams.' },
  { icon: '🤝', title: 'Personalised Mentorship', desc: 'One-to-one guidance to work through individual weak areas identified through testing.' },
  { icon: '📝', title: 'Regular Mock Tests', desc: 'A consistent testing schedule that builds the exam familiarity this strategy depends on.' },
  { icon: '📊', title: 'Performance Tracking', desc: 'Detailed analytics after every test, turning results into a clear next step rather than just a number.' },
  { icon: '💬', title: 'Doubt Solving Sessions', desc: 'Dedicated time to resolve doubts before they compound into larger gaps.' },
];

const TOC = [
  { id: 'best-strategy', label: 'Best CUET Strategy' },
  { id: 'understand-exam', label: 'Understand the Exam First' },
  { id: 'step-by-step', label: 'Step-by-Step Strategy' },
  { id: 'subject-wise', label: 'Subject-wise Guidance' },
  { id: 'thirty-day-plan', label: '30-Day Plan' },
  { id: 'mistakes', label: 'Common Mistakes' },
  { id: 'resources', label: 'Best Resources' },
  { id: 'besst-helps', label: 'How BESST Helps' },
  { id: 'comparison', label: 'Self-Study vs Coaching' },
  { id: 'expert-tips', label: 'Expert Tips' },
  { id: 'faqs', label: 'FAQs' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Prepare for CUET: Step-by-Step Strategy',
  description: 'A complete CUET preparation strategy covering syllabus analysis, study planning, NCERT fundamentals, mock tests, and revision.',
  step: STEPS.map(s => ({
    '@type': 'HowToStep',
    name: s.title,
    text: s.desc,
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://besst.in' },
    { '@type': 'ListItem', position: 2, name: 'CUET Coaching', item: 'https://besst.in/cuet-coaching' },
    { '@type': 'ListItem', position: 3, name: 'CUET Preparation Strategy', item: 'https://besst.in/cuet-preparation-strategy' },
  ],
};

const CSS = `
  .ps-page{background:linear-gradient(160deg,#0c0420 0%,#140831 50%,#0c0420 100%);min-height:100vh;color:#e8deff;font-family:'Inter','Segoe UI',system-ui,sans-serif;}
  .ps-breadcrumb{max-width:1200px;margin:0 auto;padding:1rem 1.5rem 0;font-size:.8rem;color:#9ca3af;display:flex;align-items:center;gap:.4rem;flex-wrap:wrap;}
  .ps-breadcrumb a{color:#a78bfa;text-decoration:none;}.ps-breadcrumb a:hover{color:#d4af37;}
  .ps-hero{padding:3rem 1.5rem 3.5rem;max-width:860px;margin:0 auto;text-align:center;}
  .ps-badge{display:inline-block;background:rgba(212,175,55,.12);color:#d4af37;border:1px solid rgba(212,175,55,.3);border-radius:20px;padding:.35rem 1rem;font-size:.78rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:1.25rem;}
  .ps-hero h1{font-size:clamp(1.75rem,4vw,2.7rem);font-weight:800;line-height:1.2;margin:0 0 1.25rem;color:#f0e6ff;}
  .ps-hero h1 em{font-style:normal;background:linear-gradient(90deg,#d4af37,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .ps-intro{font-size:1.05rem;line-height:1.8;color:#c4b5fd;max-width:720px;margin:0 auto 2rem;}
  .ps-cta{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}
  .btn-gold{background:linear-gradient(135deg,#d4af37,#f59e0b);color:#0c0420;font-weight:700;padding:.85rem 2rem;border-radius:10px;border:none;cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:transform .2s,box-shadow .2s;}
  .btn-gold:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(212,175,55,.35);}
  .btn-outline{background:transparent;color:#e8deff;font-weight:600;padding:.85rem 2rem;border-radius:10px;border:1.5px solid rgba(232,222,255,.25);cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:border-color .2s,background .2s;}
  .btn-outline:hover{border-color:#a78bfa;background:rgba(167,139,250,.08);}
  .ps-qa-box{background:rgba(212,175,55,.07);border:1px solid rgba(212,175,55,.2);border-radius:12px;padding:1.25rem 1.5rem;margin-bottom:2rem;}
  .ps-qa-label{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#d4af37;margin-bottom:.5rem;}
  .ps-qa-text{font-size:.93rem;line-height:1.75;color:#c4b5fd;}
  .ps-layout{max-width:1200px;margin:0 auto;padding:3rem 1.5rem;display:grid;grid-template-columns:220px 1fr;gap:3rem;align-items:start;}
  @media(max-width:900px){.ps-layout{grid-template-columns:1fr;}}
  .ps-toc{position:sticky;top:80px;}
  @media(max-width:900px){.ps-toc{display:none;}}
  .ps-toc-title{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#7c6b9e;margin-bottom:.75rem;}
  .ps-toc ul{list-style:none;padding:0;margin:0;}.ps-toc li{margin-bottom:.15rem;}
  .ps-toc button{background:none;border:none;cursor:pointer;text-align:left;width:100%;padding:.4rem .6rem;font-size:.82rem;color:#9ca3af;border-radius:6px;border-left:2px solid transparent;transition:all .15s;line-height:1.4;}
  .ps-toc button:hover{color:#e8deff;background:rgba(255,255,255,.05);}
  .ps-toc button.active{color:#d4af37;border-left-color:#d4af37;background:rgba(212,175,55,.07);font-weight:600;}
  .ps-section{margin-bottom:3.5rem;scroll-margin-top:90px;}
  .ps-h2{font-size:clamp(1.25rem,2.5vw,1.65rem);font-weight:700;color:#f0e6ff;margin:0 0 1.25rem;padding-bottom:.6rem;border-bottom:1px solid rgba(212,175,55,.2);}
  .ps-h3{font-size:1.02rem;font-weight:700;color:#d4af37;margin:1.25rem 0 .45rem;}
  .ps-p{font-size:.97rem;line-height:1.8;color:#c4b5fd;margin:0 0 1rem;}
  .ps-parent-link{display:inline-flex;align-items:center;gap:.4rem;font-size:.82rem;color:#7c6b9e;text-decoration:none;margin-bottom:.75rem;}
  .ps-parent-link:hover{color:#a78bfa;}
  .ps-bullets{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.65rem;}
  .ps-bullets li{display:flex;gap:.75rem;align-items:flex-start;font-size:.95rem;line-height:1.65;color:#c4b5fd;}
  .ps-bi{width:22px;height:22px;background:rgba(212,175,55,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.7rem;color:#d4af37;flex-shrink:0;margin-top:2px;}
  .ps-bs{color:#e8deff;font-weight:600;}
  .ps-core-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:.85rem;margin-bottom:1.25rem;}
  @media(max-width:680px){.ps-core-grid{grid-template-columns:1fr;}}
  .ps-core-card{background:rgba(124,58,237,.08);border:1px solid rgba(124,58,237,.2);border-radius:10px;padding:1rem 1.1rem;}
  .ps-core-card h3{font-size:.88rem;font-weight:700;color:#a78bfa;margin:0 0 .35rem;}
  .ps-core-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .ps-exam-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:600px){.ps-exam-grid{grid-template-columns:1fr;}}
  .ps-exam-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;display:flex;gap:.75rem;align-items:flex-start;}
  .ps-exam-icon{font-size:1.3rem;width:38px;height:38px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .ps-exam-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .ps-exam-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .ps-steps{display:flex;flex-direction:column;}
  .ps-step{display:flex;gap:1.25rem;align-items:flex-start;}
  .ps-step-left{display:flex;flex-direction:column;align-items:center;flex-shrink:0;}
  .ps-step-num{width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,rgba(212,175,55,.25),rgba(212,175,55,.1));border:2px solid rgba(212,175,55,.4);display:flex;flex-direction:column;align-items:center;justify-content:center;flex-shrink:0;}
  .ps-step-n{font-size:.68rem;font-weight:800;color:#d4af37;line-height:1;}
  .ps-step-icon{font-size:.95rem;line-height:1;}
  .ps-step-line{width:2px;flex:1;min-height:28px;background:linear-gradient(to bottom,rgba(212,175,55,.3),rgba(212,175,55,.05));}
  .ps-step:last-child .ps-step-line{display:none;}
  .ps-step-body{padding:.6rem 0 1.5rem;}
  .ps-step-body h3{font-size:.97rem;font-weight:700;color:#f0e6ff;margin:0 0 .25rem;}
  .ps-step-body p{font-size:.87rem;line-height:1.6;color:#9ca3af;margin:0;}
  .ps-plan-table{width:100%;border-collapse:collapse;font-size:.9rem;}
  .ps-plan-table thead tr{background:rgba(212,175,55,.12);}
  .ps-plan-table th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;}
  .ps-plan-table td{padding:.85rem 1.25rem;border:1px solid rgba(255,255,255,.06);color:#c4b5fd;vertical-align:top;}
  .ps-plan-table tbody tr:nth-child(even){background:rgba(255,255,255,.025);}
  .ps-plan-table tbody tr:hover{background:rgba(212,175,55,.05);}
  .ps-week-badge{display:inline-block;background:rgba(124,58,237,.2);color:#a78bfa;border-radius:6px;padding:.2rem .6rem;font-size:.8rem;font-weight:700;margin-bottom:.3rem;}
  .ps-mistakes-grid{display:grid;grid-template-columns:1fr;gap:.6rem;}
  .ps-mistake{display:flex;gap:.75rem;align-items:flex-start;background:rgba(239,68,68,.06);border:1px solid rgba(239,68,68,.15);border-radius:8px;padding:.85rem 1rem;}
  .ps-mistake-icon{color:#ef4444;font-size:1rem;flex-shrink:0;margin-top:1px;}
  .ps-mistake p{font-size:.88rem;line-height:1.55;color:#c4b5fd;margin:0;}
  .ps-mistake strong{color:#f0e6ff;}
  .ps-resources-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:600px){.ps-resources-grid{grid-template-columns:1fr;}}
  .ps-resource-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s;}
  .ps-resource-card:hover{border-color:rgba(212,175,55,.25);}
  .ps-resource-icon{font-size:1.4rem;margin-bottom:.5rem;}
  .ps-resource-card h3{font-size:.9rem;font-weight:700;color:#f0e6ff;margin:0 0 .35rem;}
  .ps-resource-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .ps-besst-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:640px){.ps-besst-grid{grid-template-columns:1fr;}}
  .ps-besst-card{display:flex;gap:.85rem;align-items:flex-start;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s,background .2s;}
  .ps-besst-card:hover{border-color:rgba(212,175,55,.25);background:rgba(255,255,255,.05);}
  .ps-besst-icon{font-size:1.4rem;width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .ps-besst-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .ps-besst-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .ps-table-wrap{overflow-x:auto;border-radius:12px;}
  .ps-cmp-table{width:100%;border-collapse:collapse;font-size:.9rem;}
  .ps-cmp-table thead tr{background:rgba(212,175,55,.12);}
  .ps-cmp-table th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;}
  .ps-cmp-table td{padding:.75rem 1.25rem;border:1px solid rgba(255,255,255,.06);color:#c4b5fd;}
  .ps-cmp-table tbody tr:nth-child(even){background:rgba(255,255,255,.025);}
  .ps-cmp-table tbody tr:hover{background:rgba(212,175,55,.05);}
  .ps-tick{color:#22c55e;font-size:1.1rem;font-weight:700;}
  .ps-cross{color:#ef4444;}
  .ps-better{color:#22c55e;font-weight:600;}
  .ps-tips-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:600px){.ps-tips-grid{grid-template-columns:1fr;}}
  .ps-tip{display:flex;gap:.65rem;align-items:flex-start;background:rgba(34,197,94,.05);border:1px solid rgba(34,197,94,.15);border-radius:8px;padding:.85rem 1rem;font-size:.88rem;line-height:1.55;color:#c4b5fd;}
  .ps-tip-check{color:#22c55e;flex-shrink:0;margin-top:1px;font-size:1rem;}
  .ps-faq-list{display:flex;flex-direction:column;gap:.6rem;}
  .ps-faq-item{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:10px;overflow:hidden;transition:border-color .2s;}
  .ps-faq-item.open{border-color:rgba(212,175,55,.3);}
  .ps-faq-q{width:100%;background:none;border:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 1.25rem;text-align:left;font-size:.93rem;font-weight:600;color:#e8deff;transition:color .15s;}
  .ps-faq-q:hover{color:#d4af37;}
  .ps-faq-chevron{font-size:.75rem;color:#7c6b9e;transition:transform .25s;flex-shrink:0;}
  .ps-faq-item.open .ps-faq-chevron{transform:rotate(180deg);color:#d4af37;}
  .ps-faq-a{max-height:0;overflow:hidden;transition:max-height .3s ease;}
  .ps-faq-item.open .ps-faq-a{max-height:500px;}
  .ps-faq-a-inner{padding:.85rem 1.25rem 1rem;font-size:.88rem;line-height:1.75;color:#9ca3af;border-top:1px solid rgba(255,255,255,.06);}
  .ps-links-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:540px){.ps-links-grid{grid-template-columns:1fr;}}
  .ps-ilink{display:flex;align-items:center;gap:.6rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:8px;padding:.7rem 1rem;text-decoration:none;color:#a78bfa;font-size:.88rem;font-weight:500;transition:border-color .2s,background .2s;}
  .ps-ilink:hover{border-color:rgba(167,139,250,.4);background:rgba(167,139,250,.06);color:#c4b5fd;}
  .ps-cta-banner{background:linear-gradient(135deg,rgba(124,58,237,.25) 0%,rgba(212,175,55,.12) 100%);border:1px solid rgba(212,175,55,.2);border-radius:16px;padding:2.5rem;text-align:center;margin-top:1rem;}
  .ps-cta-banner h2{font-size:1.5rem;font-weight:800;color:#f0e6ff;margin:0 0 .75rem;}
  .ps-cta-banner p{font-size:.95rem;line-height:1.7;color:#c4b5fd;max-width:600px;margin:0 auto 1.5rem;}
`;

const CuetPreparationStrategyPage = () => {
  const [activeSection, setActiveSection] = useState('best-strategy');
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    TOC.forEach(t => { const el = document.getElementById(t.id); if (el) observerRef.current.observe(el); });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Helmet>
        <title>CUET Preparation Strategy: Complete Guide to Crack CUET</title>
        <meta name="description" content="Learn the best CUET preparation strategy with expert tips, study plans, subject-wise guidance, and mock test strategies. Prepare smarter with BESST." />
        <meta name="keywords" content="cuet preparation strategy, how to prepare for cuet, cuet preparation tips, cuet study plan, how to crack cuet, cuet preparation at home, cuet preparation for beginners, how to prepare for cuet after class 12, cuet exam preparation, cuet preparation roadmap" />
        <meta property="og:title" content="CUET Preparation Strategy: Complete Guide to Crack CUET" />
        <meta property="og:description" content="Learn the best CUET preparation strategy with expert tips, study plans, subject-wise guidance, and mock test strategies. Prepare smarter with BESST." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://besst.in/cuet-preparation-strategy" />
        <link rel="canonical" href="https://besst.in/cuet-preparation-strategy" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="ps-page mt-20">
        <style>{CSS}</style>

        {/* Breadcrumb */}
        <nav className="ps-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span>›</span>
          <Link to="/cuet-coaching">CUET Coaching</Link><span>›</span>
          <span style={{ color: '#e8deff' }}>CUET Preparation Strategy</span>
        </nav>

        {/* Hero */}
        <div className="ps-hero">
          <div className="ps-badge">Complete Preparation Guide</div>
          <h1><em>CUET Preparation Strategy:</em> A Complete Guide to Crack CUET Successfully</h1>
          <p className="ps-intro">
            Most students preparing for CUET don't lose marks because they didn't study enough — they lose marks because they studied without a plan. This guide lays out a complete, step-by-step CUET preparation strategy: what to prioritise, how to structure your time, and where expert guidance can save weeks of trial and error.
          </p>
          <div className="ps-cta">
            <Link to="/subscriptions" className="btn-gold">Get Structured Coaching →</Link>
            <Link to="/cuet-mock-test" className="btn-outline">Try a Free Mock Test</Link>
          </div>
        </div>

        {/* Layout */}
        <div className="ps-layout">

          {/* TOC */}
          <aside className="ps-toc" aria-label="Table of contents">
            <div className="ps-toc-title">On This Page</div>
            <ul>
              {TOC.map(t => (
                <li key={t.id}>
                  <button className={activeSection === t.id ? 'active' : ''} onClick={() => scrollTo(t.id)}>{t.label}</button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main */}
          <main>
            <Link to="/cuet-coaching" className="ps-parent-link">← Back to CUET Coaching</Link>

            {/* Quick Answer box */}
            <div className="ps-qa-box">
              <div className="ps-qa-label">Quick Answer</div>
              <p className="ps-qa-text">A CUET preparation strategy is a structured plan covering syllabus analysis, time management, subject prioritisation, and regular mock testing, built to prepare a student systematically rather than reactively. The most effective plans combine NCERT-based concept building with weekly practice tests and focused revision.</p>
            </div>

            {/* Best strategy */}
            <section id="best-strategy" className="ps-section">
              <h2 className="ps-h2">What is the Best CUET Preparation Strategy?</h2>
              <p className="ps-p">There's no single formula that works for every student, but every effective CUET preparation strategy shares the same core elements:</p>
              <div className="ps-core-grid">
                {[
                  ['🔍', 'Syllabus Understanding', 'Know what\'s being tested before diving into random practice.'],
                  ['📐', 'Exam Pattern Awareness', 'Section structure, timing, and marking — nothing should feel unfamiliar on exam day.'],
                  ['⏱️', 'Time Management', 'In daily sessions and within the timed exam itself.'],
                  ['🎯', 'Subject Prioritisation', 'More time on weaker or higher-weightage areas, not even spread.'],
                  ['🔄', 'Consistent Revision', 'Spaced over months, not crammed at the end.'],
                  ['📝', 'Mock Test Practice', 'Repeated often enough that the format feels routine, not threatening.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="ps-core-card">
                    <h3>{icon} {title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
              <p className="ps-p">The strategy that works best is rarely the most intense one — it's the one a student can actually sustain for four to six months without burning out.</p>
            </section>

            {/* Understand the exam */}
            <section id="understand-exam" className="ps-section">
              <h2 className="ps-h2">Understand the CUET Exam Before You Start Preparing</h2>
              <p className="ps-p">Understanding the exam this closely before diving into study material isn't a delay tactic — it's what makes every study hour afterward more targeted and efficient.</p>
              <div className="ps-exam-grid">
                {[
                  ['📋', 'CUET Exam Pattern', 'CUET is structured around a language test, domain subjects, and a general test, each with its own section and timing. Understanding this prevents over-preparing one section at the cost of another.'],
                  ['📌', 'Subject Selection', 'Students choose domain subjects based on their target undergraduate programmes — this choice should happen early, since it directly determines what to study.'],
                  ['➕', 'Marking Scheme', 'Knowing exactly how marks are awarded per correct answer shapes how a student should approach each question, particularly under time pressure.'],
                  ['⚠️', 'Negative Marking', 'CUET applies negative marking, which changes the calculus around guessing. Understanding this early helps students develop the right instincts during practice.'],
                  ['⏰', 'Time Allocation', 'Each section has a fixed time limit. Practising within that limit — not just answering questions untimed — is one of the most overlooked parts of preparation.'],
                  ['📚', 'Syllabus Depth', 'Knowing which topics carry more weight within each subject helps prioritise study time where it counts most.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="ps-exam-card">
                    <div className="ps-exam-icon">{icon}</div>
                    <div><h3>{title}</h3><p>{desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Step by step */}
            <section id="step-by-step" className="ps-section">
              <h2 className="ps-h2">Step-by-Step CUET Preparation Strategy</h2>
              <div className="ps-steps">
                {STEPS.map(s => (
                  <div key={s.n} className="ps-step">
                    <div className="ps-step-left">
                      <div className="ps-step-num">
                        <span className="ps-step-n">{s.n}</span>
                        <span className="ps-step-icon">{s.icon}</span>
                      </div>
                      <div className="ps-step-line" />
                    </div>
                    <div className="ps-step-body"><h3>{s.title}</h3><p>{s.desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Subject-wise */}
            <section id="subject-wise" className="ps-section">
              <h2 className="ps-h2">Subject-wise CUET Preparation Strategy</h2>

              <h3 className="ps-h3">Language Section</h3>
              <p className="ps-p">Build reading speed through regular comprehension practice, strengthen vocabulary steadily rather than through last-minute lists, and practise within the section's actual time limit. The language section rewards consistent daily reading more than intensive cramming sessions.</p>

              <h3 className="ps-h3">Domain Subjects</h3>
              <p className="ps-p">Anchor preparation in NCERT, supplement with topic-wise practice questions, and revisit high-weightage chapters more than once through the preparation window. CUET's domain subject questions map closely to NCERT — students who build concepts here first consistently outperform those who jump straight to practice material.</p>

              <h3 className="ps-h3">General Test</h3>
              <p className="ps-p">The general test covers current affairs, logical reasoning, and quantitative aptitude. Current affairs requires ongoing attention throughout the preparation window, not just coverage in the final weeks. Reasoning and aptitude respond well to regular timed practice.</p>
            </section>

            {/* 30-day plan */}
            <section id="thirty-day-plan" className="ps-section">
              <h2 className="ps-h2">30-Day CUET Preparation Plan</h2>
              <p className="ps-p" style={{ marginBottom: '1rem' }}>This plan works best as a final-month structure layered on top of earlier, longer-term preparation.</p>
              <div className="ps-table-wrap">
                <table className="ps-plan-table">
                  <thead>
                    <tr><th style={{ width: '130px' }}>Week</th><th>Focus Area</th><th>Key Activities</th></tr>
                  </thead>
                  <tbody>
                    {[
                      ['Week 1', 'Syllabus Understanding & Concept Building', 'Review syllabus by subject, complete first pass of NCERT chapters, map weak areas.'],
                      ['Week 2', 'Topic-wise Practice', 'Solve focused question sets after each topic, revisit NCERT where gaps appear, begin timed topic tests.'],
                      ['Week 3', 'Mock Tests & Revision', 'Take at least two full-length mocks, analyse mistakes section by section, revise weak topics from Week 1.'],
                      ['Week 4', 'Full-length Mocks & Final Revision', 'Two to three full mocks in exam conditions, targeted revision of lowest-scoring areas, consolidate rather than introduce new material.'],
                    ].map(([week, focus, activities]) => (
                      <tr key={week}>
                        <td><span className="ps-week-badge">{week}</span></td>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{focus}</td>
                        <td>{activities}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="ps-p" style={{ marginTop: '1rem', fontSize: '.88rem' }}>
                ℹ️ Students starting from zero thirty days out should prioritise ruthlessly rather than covering everything in equal depth. Longer preparation windows (4–6 months) allow this plan to be spread across the full syllabus systematically.
              </p>
            </section>

            {/* Common mistakes */}
            <section id="mistakes" className="ps-section">
              <h2 className="ps-h2">Common Mistakes to Avoid During CUET Preparation</h2>
              <div className="ps-mistakes-grid">
                {[
                  ['Ignoring NCERT books', 'Favouring scattered reference material over NCERT, which is the primary source CUET questions are drawn from.'],
                  ['Skipping mock tests until the final weeks', 'Losing months of format familiarity that regular testing would have built incrementally.'],
                  ['Not analysing mistakes after practice tests', 'Repeating the same errors across multiple tests because the root cause was never identified.'],
                  ['Over-relying on shortcuts', 'Learning tricks without understanding the underlying concept — fragile under exam pressure.'],
                  ['Poor time management', 'Both in daily study sessions and within timed practice — familiarity with section timing must be practised, not assumed.'],
                  ['Last-minute cramming', 'Compressing into weeks what should have been spread across months — information recalled under rush rarely sticks under pressure.'],
                  ['Ignoring revision', 'Treating a first pass through a topic as sufficient — topics covered early in preparation fade without deliberate revisiting.'],
                ].map(([title, desc]) => (
                  <div key={title} className="ps-mistake">
                    <span className="ps-mistake-icon">✕</span>
                    <p><strong>{title}: </strong>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Resources */}
            <section id="resources" className="ps-section">
              <h2 className="ps-h2">Best Resources for CUET Preparation</h2>
              <div className="ps-resources-grid">
                {[
                  ['📚', 'NCERT Books', 'The foundational resource for most CUET domain subjects — concepts here map directly onto what is tested.'],
                  ['📄', 'Previous Year Question Papers', "Essential for understanding NTA's actual question style and commonly tested areas."],
                  ['📝', 'Mock Tests', 'The closest simulation of exam-day conditions available before the real thing.'],
                  ['📋', 'Sample Papers', 'Useful for additional practice once NCERT concepts and PYQ patterns are well understood.'],
                  ['🌐', 'Online Learning Platforms', 'Structured platforms pull these resources into one coherent plan — where BESST\'s study materials and mock test programme are designed to fit directly into this strategy.'],
                  ['🗂️', 'Topic-wise Practice Sets', 'Focused sets after each topic, catching gaps early rather than at the full-mock stage.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="ps-resource-card">
                    <div className="ps-resource-icon">{icon}</div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* How BESST helps */}
            <section id="besst-helps" className="ps-section">
              <h2 className="ps-h2">How BESST Helps You Prepare for CUET</h2>
              <p className="ps-p">BESST supports students through exactly this preparation process, with structured study plans, live classes, and regular assessments that turn a strategy like this one from a checklist into an actual habit.</p>
              <div className="ps-besst-grid">
                {BESST_FEATURES.map(f => (
                  <div key={f.title} className="ps-besst-card">
                    <div className="ps-besst-icon">{f.icon}</div>
                    <div><h3>{f.title}</h3><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
                <Link to="/online-cuet-coaching" className="btn-gold">Explore BESST's CUET Online Coaching →</Link>
              </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="ps-section">
              <h2 className="ps-h2">Online Coaching vs Self-Study – Which is Better?</h2>
              <div className="ps-table-wrap">
                <table className="ps-cmp-table">
                  <thead><tr><th>Factors</th><th>Self Study</th><th>BESST Online Coaching</th></tr></thead>
                  <tbody>
                    {[
                      ['Structured Learning', 'Limited', '✔'],
                      ['Expert Guidance', '✘', '✔'],
                      ['Mock Tests', 'Limited', '✔'],
                      ['Performance Tracking', '✘', '✔'],
                      ['Doubt Solving', '✘', '✔'],
                      ['Personal Mentorship', '✘', '✔'],
                    ].map(([factor, self, besst]) => (
                      <tr key={factor}>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{factor}</td>
                        <td style={{ color: '#9ca3af' }}>{self}</td>
                        <td className="ps-better">{besst}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="ps-p" style={{ marginTop: '1rem' }}>
                Self-study can work for highly disciplined students comfortable building their own schedule and staying consistent without external accountability. For most students, though, the gap isn't ability — it's structure and feedback, which is exactly where guided coaching tends to add the most value.
              </p>
            </section>

            {/* Expert tips */}
            <section id="expert-tips" className="ps-section">
              <h2 className="ps-h2">Expert Tips to Crack CUET in the First Attempt</h2>
              <div className="ps-tips-grid">
                {[
                  'Study consistently instead of cramming close to the exam',
                  'Focus on understanding concepts, not just memorising answers',
                  'Revise frequently, in short repeated sessions rather than one long pass',
                  'Practise mock tests under timed conditions, not open-ended ones',
                  'Analyse every mock test, not just the ones you did poorly on',
                  'Maintain a healthy study-life balance — burnout undermines consistency',
                  'Stay updated with official notifications so nothing catches you off guard',
                ].map(tip => (
                  <div key={tip} className="ps-tip"><span className="ps-tip-check">✓</span><span>{tip}</span></div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="ps-section">
              <h2 className="ps-h2">Frequently Asked Questions (FAQs)</h2>
              <div className="ps-faq-list">
                {FAQS.map((f, i) => (
                  <div key={i} className={`ps-faq-item ${openFaq === i ? 'open' : ''}`}>
                    <button className="ps-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                      <span>{f.q}</span><span className="ps-faq-chevron">▼</span>
                    </button>
                    <div className="ps-faq-a" aria-hidden={openFaq !== i}>
                      <div className="ps-faq-a-inner">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Internal links */}
            <section className="ps-section">
              <h2 className="ps-h2">Explore More CUET Resources</h2>
              <div className="ps-links-grid">
                {[
                  ['/cuet-coaching', '📚 CUET Coaching (Pillar)'],
                  ['/online-cuet-coaching', '💻 Online CUET Coaching'],
                  ['/best-cuet-coaching-in-assam', '📍 Best CUET Coaching in Assam'],
                  ['/cuet-mock-test', '📝 CUET Mock Test'],
                  ['/cuet-mock-test/free', '🆓 Free CUET Mock Test'],
                  ['/cuet-study-plan', '🗓️ CUET Study Plan'],
                  ['/how-to-crack-cuet', '🎯 How to Crack CUET'],
                  ['/cuet-syllabus', '📋 CUET Syllabus'],
                  ['/cuet-exam-pattern', '📐 CUET Exam Pattern'],
                  ['/cuet-registration', '📝 CUET Registration'],
                  ['/cuet-subject-list', '📑 CUET Subject List'],
                  ['/cuet-previous-year-question-papers', '📄 CUET PYQ Papers'],
                  ['/cuet-cutoff', '📊 CUET Cutoff'],
                  ['/cuet-counselling', '🎓 CUET Counselling'],
                ].map(([to, label]) => (
                  <Link key={to} to={to} className="ps-ilink">
                    <span>{label}</span>
                    <span style={{ marginLeft: 'auto', opacity: .5 }}>→</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="ps-cta-banner">
              <h2>Ready to Follow a Structured CUET Preparation Strategy?</h2>
              <p>
                A structured preparation strategy consistently outperforms simply studying harder. BESST's CUET Online Coaching combines expert-led live classes, comprehensive study materials, mock tests, personalised mentorship, and performance tracking to help you prepare with confidence.
              </p>
              <Link to="/subscriptions" className="btn-gold">Start Structured CUET Preparation →</Link>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default CuetPreparationStrategyPage;
